<template>
  <v-card class="step-tab-panel" flat>
    <v-row class="ma-0">
      <v-col class="pa-0 step-tab-panel__content justify">
        <ExtractReportGenerator
          :disabled="isGenerateExtractDisabled"
          @generate-extract-requested="generateReport"
        >
          <v-container class="report-product-store-extract d-flex flex-column pa-0 ma-0" fluid>
            <v-row no-gutters class="ma-0">
              <v-col class="margin-right-23 colspan">
                <ReportInputItem :title="t('extract.reports.productStore.reportType.title')">
                  <div>
                    <v-select
                      v-model="selectedExtract"
                      :items="extractions"
                      hide-details
                      density="compact"
                      append-icon="expand_more"
                    />
                  </div>
                </ReportInputItem>
              </v-col>
              <v-col class="colspan">
                <ReportInputItem :title="t('extract.reports.productStore.extractName.name')">
                  <v-text-field
                    v-model="extractName"
                    class="rtls-text-field--grey"
                    :placeholder="t('extract.reports.productStore.extractName.placeholder')"
                    :hint="uniqueNameHint"
                    :rules="nameRules"
                  />
                </ReportInputItem>
              </v-col>
            </v-row>

            <v-row class="mt-6">
              <CheckpointsSelectionTable
                :scenario-id="selectedScenario._id"
                :headers="headers"
                :canvases="items"
                :checkpoints-by-canvas-id="checkpointsByCanvasId"
                :title="t('extract.reports.productStore.table.title')"
                @update-selected-checkpoints="setSelectedCheckpoints"
              />
            </v-row>
          </v-container>
        </ExtractReportGenerator>
      </v-col>
      <v-col cols="4" class="pt-0 pr-0 justify">
        <PreviousExtractReports :reports="reports" />
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { useInputValidations } from '@/composables/input-validations';
import { useDayjs } from '@/composables/use-dayjs';
import { useInternationalization } from '@/composables/use-internationalization';
import { usePermission } from '@/composables/use-permission';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import { useContextStore } from '@/stores/context';
import { useScenariosStore } from '@/stores/scenarios';
import { useExtractsStore } from '@/stores/extracts';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { debounce, every, forEach, get, includes, isEmpty, map } from 'lodash';
import { mapActions, mapState } from 'pinia';

import extractsUtils from '@/utils/extracts-utils';
import clientEvents from '@enums/client-events';
import extractNames from '@enums/extract-names';
import extractTypes from '@enums/extract-types';
import jobapi from '@enums/jobapi';

const { PRODUCT_INFO, EXTERNAL_OPTIMISATION_EXTRACT } = extractTypes;
const { PRODUCT_OUTBOUND_API_EXTRACT } = extractNames;

dayjs.extend(utc);

const canvasesFilterParams = {
  where: { live: true },
  pick: ['clusterId', 'jobs', 'checkpointMeta', 'live'],
};

export default {
  setup() {
    const { t, tkey } = useInternationalization('extract.reports.productStore');
    const { canEditProductStoreExtracts } = usePermission();
    const { required } = useInputValidations();
    const { createDateForSave, formatDate } = useDayjs();

    return { t, tkey, canEditProductStoreExtracts, required, formatDate, createDateForSave };
  },
  data() {
    return {
      dayjs,
      levels: {
        product: true,
        store: false,
      },
      selectedExtract: null,
      extractions: [],
      headers: [
        {
          title: this.t('checkpointSelectionTable.headers.cluster'),
          value: 'cluster',
          width: 100,
        },
        {
          title: this.t('checkpointSelectionTable.headers.storeClass'),
          value: 'storeClass',
          width: 100,
        },
        {
          title: this.t('checkpointSelectionTable.headers.description'),
          value: 'description',
          width: 200,
        },
        {
          title: this.t('checkpointSelectionTable.headers.optimised'),
          value: 'status',
          align: 'center',
          width: 100,
        },
        {
          title: this.t('checkpointSelectionTable.headers.checkpoint'),
          value: 'checkpoint',
          width: 250,
        },
      ],
      extractName: null,
      selectedCheckpoints: {},
      isCheckpointError: false,
    };
  },

  computed: {
    ...mapState(useScenariosStore, ['selectedScenario']),
    ...mapState(useExtractsStore, ['extracts']),
    ...mapState(useAssortmentCanvasStore, ['scenarioCheckpointsByCanvasId', 'canvases']),
    ...mapState(useContextStore, ['getDateFormats', 'getClientConfig']),

    items() {
      return map(this.canvases, c => ({
        _id: c._id,
        clusterId: c.clusterId,
        cluster: c.clusterName || this.tkey('unClustered'),
        storeClassId: c.storeClassId,
        storeClass: c.storeClassName,
        description: c.description || this.tkey('noDescription'),
        status: c.hasBeenOptimised
          ? jobapi.jobStatusesIconsMappings[jobapi.jobStatuses.finished]
          : jobapi.jobStatusesIconsMappings[jobapi.jobStatuses.failed],
        originalJobStatus:
          c.jobs && c.jobs.assortmentOptimisation
            ? c.jobs.assortmentOptimisation.status
            : jobapi.jobStatuses.failed,
        checkpoint: null,
      }));
    },

    checkpointsByCanvasId() {
      // Create a map of canvas id to checkpoint options
      const checkpointsByCanvasId = {};
      forEach(this.items, ({ _id: canvasId }) => {
        const canvasCheckpoints = get(this.scenarioCheckpointsByCanvasId, canvasId, []).map(
          ({ checkpointMeta, _id }) => {
            return {
              text: checkpointMeta.name,
              value: _id,
            };
          }
        );
        canvasCheckpoints.unshift({ text: this.tkey('noSelection'), value: null });
        checkpointsByCanvasId[canvasId] = canvasCheckpoints;
      });

      return checkpointsByCanvasId;
    },

    isGenerateExtractDisabled() {
      return (
        isEmpty(this.scenarioCheckpointsByCanvasId) ||
        !this.canEditProductStoreExtracts ||
        this.isCheckpointError ||
        !this.selectedExtract ||
        (this.selectedExtract === PRODUCT_OUTBOUND_API_EXTRACT && !this.extractName)
      );
    },

    reports() {
      return extractsUtils.reports({
        extracts: this.extracts,
        reportType: this.selectedExtractType,
      });
    },

    uniqueNameHint() {
      return this.selectedExtract === PRODUCT_OUTBOUND_API_EXTRACT
        ? this.tkey('reportType.uniqueNameWarning')
        : '';
    },

    nameRules() {
      return this.selectedExtract === PRODUCT_OUTBOUND_API_EXTRACT ? [this.required] : [];
    },

    selectedExtractType() {
      return this.selectedExtract === PRODUCT_OUTBOUND_API_EXTRACT
        ? EXTERNAL_OPTIMISATION_EXTRACT
        : PRODUCT_INFO;
    },

    extractSelection() {
      if (this.selectedExtract === PRODUCT_OUTBOUND_API_EXTRACT)
        return EXTERNAL_OPTIMISATION_EXTRACT;
      return this.selectedExtract;
    },

    reportGenerationOptions() {
      return {
        filenameOnDownload:
          this.extractName ||
          this.t('extract.reports.defaultExtractFileName', [
            this.formatDate(this.dayjs(), this.getDateFormats.longWithTime),
          ]),
        extractType: this.selectedExtractType,
        extractDatetime: this.createDateForSave(),
        fileId: null,
        canvasesIds: Object.values(this.selectedCheckpoints).filter(c => c),
        extractsSelection: [this.extractSelection],
      };
    },
  },

  watch: {
    items() {
      if (this.shouldPollCanvases()) {
        this.debouncedFetchCanvases({ params: canvasesFilterParams });
      }
    },
  },

  async created() {
    const availableProductExtracts = get(
      this.getClientConfig,
      `extracts.${extractTypes.PRODUCT_INFO}`,
      []
    );
    this.extractions = map(availableProductExtracts, e => ({
      title: this.tkey(`reportType.extractions.${e}`),
      value: e,
    }));
    this.selectedExtract = availableProductExtracts[0] || null;
    this.debouncedFetchCanvases = debounce(this.fetchCanvases, 5000);
    await Promise.all([
      this.fetchCanvases({ params: canvasesFilterParams }),
      this.fetchScenarioCheckpoints({ excludeObserved: false }),
    ]);
  },

  methods: {
    ...mapActions(useAssortmentCanvasStore, ['fetchCanvases', 'fetchScenarioCheckpoints']),
    ...mapActions(useExtractsStore, ['generateExtract']),

    shouldPollCanvases() {
      // checks if there are unfinished jobs that need to be refreshed
      return (
        every(this.items, i => includes(jobapi.jobFinishedStatuses, i.originalJobStatus)) === false
      );
    },

    async generateReport() {
      if (!this.checkpointError) {
        await this.generateExtract(this.reportGenerationOptions);
        this.globalEmit(clientEvents.extracts.extractGenerated);
      }
    },

    setSelectedCheckpoints(cps) {
      this.selectedCheckpoints = cps;
      // validate the selected checkpoints
      this.isCheckpointError = Object.values(cps).every(v => !v);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.report-product-store-extract {
  .margin-right-23 {
    margin-right: 23px;
  }
}

.rtls-checkbox {
  margin: 0 !important;
  :deep(.v-input--selection-controls__input) {
    margin: 0 !important;
  }
}

.width-100 {
  width: 100%;
}

.border-selected {
  border: 1px solid $assortment-primary-colour;
}

.border-unselected {
  border: 1px solid $assortment-divider-colour;
}

.checkbox-container {
  border-radius: 4px;
  padding: 2px 1px 2px 7px;
}

.checkbox-label {
  font-size: 1.2rem;
  color: $assortment-font-colour;
}

.step-tab-panel {
  background-color: $assortment-background !important;

  &__content {
    background: $assortment-panel-background;
  }
}
</style>
